import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AUTH_FETCH_TIMEOUT_MS,
  CF_IDENTITY_URL,
  fetchCfIdentity,
  isCfAuthRedirect,
} from '../lib/api';

// DDD #107 S3 T3.3 — useAuthSession hook
// Spec: vault/tmp/2026-05-16_daedalus-fe-ddd107-s3-useauthsession-prestage/useAuthSession_signature_spec.md

export interface UseAuthSession {
  email: string | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

const REFRESH_MS = 15 * 60 * 1000;
const BACKOFF_MS = [0, 500, 1000, 2000, 4000] as const;
const REDIRECT_LOOP_WINDOW_MS = 60_000;
const REDIRECT_LOOP_MAX = 3;
const REDIRECT_ATTEMPTS_KEY = 'tm_redirect_attempts';
const REDIRECT_TS_KEY = 'tm_redirect_ts';

interface AuthBody {
  email?: string;
}

type FetchAttempt = (signal: AbortSignal) => Promise<Response>;

// Test seam · jsdom marks window.location.assign non-configurable, so we route through
// an overridable function reference. Tests reassign __testing.navigate; production uses the default.
export const __testing = {
  navigate: (href: string): void => {
    window.location.assign(href);
  },
};

function readNum(key: string): number {
  try {
    return Number(sessionStorage.getItem(key) ?? '0');
  } catch {
    return 0;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* sessionStorage blocked · best-effort */
  }
}

function removeStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

function shouldHaltRedirect(): boolean {
  const ts = readNum(REDIRECT_TS_KEY);
  const attempts = readNum(REDIRECT_ATTEMPTS_KEY);
  return Date.now() - ts < REDIRECT_LOOP_WINDOW_MS && attempts >= REDIRECT_LOOP_MAX;
}

function recordRedirectAttempt(): void {
  const ts = readNum(REDIRECT_TS_KEY);
  const attempts = readNum(REDIRECT_ATTEMPTS_KEY);
  const now = Date.now();
  const next = now - ts < REDIRECT_LOOP_WINDOW_MS ? attempts + 1 : 1;
  writeStorage(REDIRECT_ATTEMPTS_KEY, String(next));
  writeStorage(REDIRECT_TS_KEY, String(now));
}

function clearRedirectAttempts(): void {
  removeStorage(REDIRECT_ATTEMPTS_KEY);
  removeStorage(REDIRECT_TS_KEY);
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException('aborted', 'AbortError'));
    };
    const t = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

async function fetchWithTimeout(
  fetcher: FetchAttempt,
  parentSignal: AbortSignal,
): Promise<Response> {
  const ctrl = new AbortController();
  const onParentAbort = () => ctrl.abort();
  parentSignal.addEventListener('abort', onParentAbort, { once: true });
  const timer = setTimeout(() => ctrl.abort(), AUTH_FETCH_TIMEOUT_MS);
  try {
    return await fetcher(ctrl.signal);
  } finally {
    clearTimeout(timer);
    parentSignal.removeEventListener('abort', onParentAbort);
  }
}

// Retry primary/fallback fetch with exponential backoff on transient errors.
// Returns the Response on success/auth-redirect/non-retriable. Throws on exhausted retries.
async function fetchWithRetry(fetcher: FetchAttempt, signal: AbortSignal): Promise<Response> {
  let lastErr: unknown;
  for (let i = 0; i < BACKOFF_MS.length; i++) {
    if (signal.aborted) throw new DOMException('aborted', 'AbortError');
    if (BACKOFF_MS[i] > 0) {
      await delay(BACKOFF_MS[i], signal);
    }
    try {
      const res = await fetchWithTimeout(fetcher, signal);
      if (signal.aborted) throw new DOMException('aborted', 'AbortError');
      if (isCfAuthRedirect(res)) return res;
      if (res.ok) return res;
      if (res.status >= 500 || res.status === 429) {
        lastErr = new Error(`transient ${res.status}`);
        continue;
      }
      return res;
    } catch (err) {
      if (signal.aborted) throw err;
      lastErr = err;
      continue;
    }
  }
  throw lastErr ?? new Error('retries exhausted');
}

export function useAuthSession(): UseAuthSession {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const ctrlRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);
  const mountedRef = useRef(false);

  const run = useCallback(async (): Promise<void> => {
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const myRunId = ++runIdRef.current;
    const isStale = () => !mountedRef.current || myRunId !== runIdRef.current;

    setLoading(true);
    setError(null);

    const triggerRedirect = (): void => {
      if (isStale()) return;
      if (shouldHaltRedirect()) {
        setEmail(null);
        setError(new Error('REDIRECT_LOOP_DETECTED'));
        setLoading(false);
        return;
      }
      recordRedirectAttempt();
      try {
        __testing.navigate(window.location.href);
      } catch {
        setError(new Error('redirect failed'));
        setLoading(false);
      }
    };

    const finishAuthed = (e: string): void => {
      if (isStale()) return;
      clearRedirectAttempts();
      setEmail(e);
      setError(null);
      setLoading(false);
    };

    const finishError = (err: unknown): void => {
      if (isStale()) return;
      setEmail(null);
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    };

    let primaryRes: Response | null = null;
    let primaryErr: unknown = null;
    try {
      primaryRes = await fetchWithRetry((s) => fetchCfIdentity(s), ctrl.signal);
    } catch (e) {
      primaryErr = e;
    }
    if (isStale()) return;

    if (primaryRes && primaryRes.ok && !isCfAuthRedirect(primaryRes)) {
      try {
        const body = (await primaryRes.json()) as AuthBody;
        if (isStale()) return;
        if (body.email) {
          finishAuthed(body.email);
          return;
        }
        finishError(new Error('primary response missing email'));
        return;
      } catch (e) {
        finishError(e);
        return;
      }
    }

    if (primaryRes && isCfAuthRedirect(primaryRes)) {
      // Per spec §3 · primary auth-redirect → redirect immediately (gateway also gated by CF).
      triggerRedirect();
      return;
    }

    // Primary exhausted — CF Access is the sole auth path for beta.
    // Fallback removed: /api/auth/me is not registered in tm-gateway (Codex P2 finding).
    finishError(primaryErr ?? new Error('auth check failed'));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void run();
    const interval = window.setInterval(() => void run(), REFRESH_MS);
    return () => {
      mountedRef.current = false;
      ctrlRef.current?.abort();
      window.clearInterval(interval);
    };
  }, [run]);

  const refetch = useCallback((): void => {
    void run();
  }, [run]);

  return { email, loading, error, refetch };
}

// Exported for tests · NOT public API
export const __internals = {
  REFRESH_MS,
  BACKOFF_MS,
  REDIRECT_LOOP_WINDOW_MS,
  REDIRECT_LOOP_MAX,
  REDIRECT_ATTEMPTS_KEY,
  REDIRECT_TS_KEY,
  CF_IDENTITY_URL,
};
